import { useEffect, useMemo, useState } from 'react';
import { XIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { isImageFile } from '~/modules/file';
import { ChatLightbox, type LightboxItem } from '../lightbox/ChatLightbox';

/**
 * The attachments for the chat input.
 * @see {@link ChatInput}
 */
export function ChatInputAttachments({
    className,
    attachments,
    onAttachmentRemove,
}: {
    className?: string;
    attachments: File[];
    onAttachmentRemove: (index: number) => void;
}) {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    // Lightbox items for images.
    const lightboxItems = useMemo(() => {
        return attachments
            .map((attachment, index) => ({
                id: index, // Assign a unique ID for the lightbox
                attachment,
            }))
            .filter(({ attachment }) => isImageFile(attachment.name))
            .map<LightboxItem>(({ id, attachment }) => ({
                id,
                url: URL.createObjectURL(attachment),
                filename: attachment.name,
            }));
    }, [attachments]);

    function openLightbox(index: number) {
        const lightBoxIndex = lightboxItems.findIndex(
            (item) => item.id === index
        );
        if (lightBoxIndex >= 0) {
            setLightboxIndex(lightBoxIndex);
            setLightboxOpen(true);
        }
    }

    // Clean up the object URLs to prevent memory leaks.
    useEffect(() => {
        return () => {
            for (const item of lightboxItems) {
                URL.revokeObjectURL(item.url);
            }
        };
    }, [lightboxItems]);

    return (
        <>
            <ul
                className={cn(
                    'flex flex-wrap content-start items-center gap-[8px]',
                    className
                )}
            >
                {attachments.map((attachment, index) => (
                    <AttachmentItem
                        key={`${attachment.name}-${attachment.size}-${attachment.lastModified}`}
                        className="max-w-[calc(50%-4px)]"
                        attachment={attachment}
                        onImageClick={() => openLightbox(index)}
                        onRemove={() => onAttachmentRemove(index)}
                    />
                ))}
            </ul>
            {lightboxItems.length > 0 && (
                <ChatLightbox
                    open={lightboxOpen}
                    index={lightboxIndex}
                    items={lightboxItems}
                    onOpenChange={setLightboxOpen}
                    onIndexChange={setLightboxIndex}
                />
            )}
        </>
    );
}

function AttachmentItem({
    className,
    attachment,
    onImageClick,
    onRemove,
}: {
    className?: string;
    attachment: File;
    onImageClick: () => void;
    onRemove: () => void;
}) {
    const imageUrl = useMemo(() => {
        return isImageFile(attachment.name)
            ? URL.createObjectURL(attachment)
            : null;
    }, [attachment]);

    // Clean up the object URL to prevent memory leaks.
    useEffect(() => {
        return () => {
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
            }
        };
    }, [imageUrl]);

    return (
        <li
            className={cn(
                'group relative rounded-[4px] bg-gray-100',
                className
            )}
        >
            {imageUrl ? (
                <div className="group/image relative size-[52px] overflow-hidden rounded-[inherit]">
                    <img
                        className="size-full cursor-zoom-in select-none object-cover"
                        src={imageUrl}
                        alt={attachment.name}
                        draggable={false}
                        onClick={onImageClick}
                    />
                    {/* Image attachment overlay. */}
                    <div className="pointer-events-none absolute inset-0 hidden bg-black/5 group-hover/image:block"></div>
                </div>
            ) : (
                <div className="truncate rounded-[inherit] p-[8px] text-[12px] leading-tight">
                    {attachment.name}
                </div>
            )}
            <button
                type="button"
                className="invisible absolute right-[-8px] top-[-8px] z-1 grid size-[18px] place-items-center rounded-full bg-gray-600 text-white hover:bg-gray-800 group-hover:visible"
                aria-label="Remove attachment"
                onClick={onRemove}
            >
                <XIcon className="size-[10px]" />
            </button>
        </li>
    );
}
